import ConvertUtil from "utils/ConvertUtil.js";
import UnitService from "services/UnitService.js";
import sum from "lodash/sum.js";
import isNil from "lodash/isNil.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {getLocalizedUnit} from "utils/UnitLabelUtil.js";
import type {
  TranslateFunction,
  ChallengeDistanceParam,
  ChallengeActivityEntry,
  ChallengeBestStat
} from "./AppConstants.types.js";

export type {
  TranslateFunction,
  ChallengeDistanceParam,
  ChallengeActivityEntry,
  ChallengeBestStat
} from "./AppConstants.types.js";

export const MESSAGES = {
  resetPasswordSuccess: "Password reset email sent successfully.",
  updateSuccess: "Successfully Updated",
  invalidCredentail: "Invalid username or password!",
  error: "Some Error Occured!",
  unauthorizedUser: "You are not authorized person..."
};

export const PAGE_URLS = {
  signInPageUrl: "/sign-in",
  signUpPageUrl: "/sign-up",
  onboardingPageUrl: "/onboarding",
  payPageUrl: "/pay",
  oldDiaryPageUrl: "/diary",
  diaryPageUrl: "/activities",
  compareActivitiesPageUrl: "/compare-activities",
  statisticsPageUrl: "/statistics",
  androidSettingsPageUrl: "/android-settings",
  challengesPageUrl: "/challenges",
  activityPageUrl: "/activity",
  livePageUrl: "/live",
  myAccountPageUrl: "/my-account",
  aroundMePageUrl: "/around-me",
  publicAroundMePageUrl: "/public/around-me",
  publicExplorePageUrl: "/explore",
  publicNavigationPageUrl: "/public/navigation",
  publicChallengeLeaderboardPageUrl: "/public/challenge-leaderboard",
  forgetPasswordPageUrl: "/forget-password",
  unauthorizedPageUrl: "/unauthorized",
  subscriptionExpirePageUrl: "/subscription-expire",
  adminPageUrl: "/admin-page",
  logsPageUrl: "/logs",
  equipmentsPageUrl: "/equipments",
  usersPageUrl: "/users",
  trashPageUrl: "/trash",
  refusedActivitiesPageUrl: "/refused-activities",
  publicRedirectApp: "/redirect-app",
  redeemCode: "/redeem-code",
  recordPageUrl: "/record",
  settingsPageUrl: "/settings",
  settingsSubscriptionPageUrl: "/settings/subscription",
  favoritesPageUrl: "/favorites",
  localExpertsPageUrl: "/local-experts",
  adminActivitiesPageUrl: "/activities-search",
  autoTrimPageUrl: "/auto-trim",
  connectServicePageUrl: "/connect-service"
};

// Deep link opened by the mobile connect page to hand control back to the native apps
export const WATERSPEED_APP_DEEPLINK = "wsapp://";

export const ANDROID_TABS = {
  DIARY: "DIARY",
  CHALLENGES: "CHALLENGES",
  RECORD: "RECORD",
  EXPLORE: "EXPLORE",
  ACCOUNT: "ACCOUNT"
};

export const SUPPORT_MAIL = "support@waterspeedapp.com";

export const WATERSPEED_WEBSITE_LINK = "https://waterspeedapp.com";

export const WATERSPEED_APP_STORE_LINK = "https://itunes.apple.com/app/apple-store/id1234093389?mt=8";
export const WATERSPEED_PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.waterspeed.waterspeedapp";

export const WATERSPEED_FAQ_LINK = "https://waterspeed.crunch.help";

export const WATERSPEED_PRIVACY_LINK = "https://waterspeedapp.com/privacy-policy/";
export const WATERSPEED_TERMS_LINK = "https://waterspeedapp.com/terms/";
export const WATERSPEED_REFUND_LINK = "https://waterspeedapp.com/refund-policy";
/** Full Free / Pro / Ultra feature comparison on the marketing site. */
export const WATERSPEED_PLANS_LINK = "https://www.waterspeedapp.com/plans";
/** Static pricing page (public/pricing.html), served outside the React app. */
export const WATERSPEED_PRICING_PATH = "/pricing";

export const ACTIVITY_PAGE_BUTTONS = {
  foilingTracks: "foilingTracks",
  jibes: "jibes",
  tacks: "tacks",
  cutTrack: "cutTrack",
  partialTrack: "partialTrack",
  more: "more"
};

export const ADMIN_PAGE_BUTTONS = {
  equip: "equip",
  users: "users"
};

export const DB_COLLECTION_NAMES = {
  users: "Users",
  points: "Points",
  navs: "Nav",
  strokes: "Strokes",
  logs: "Logs",
  equip: "Equipment",
  appData: "AppData",
  sharedData: "SharedData",
  garminData: "GarminData",
  suuntoData: "SuuntoData",
  corosData: "CorosData",
  redeemCodes: "RedeemCodes",
  waitingApprovalActivities: "WaitingApprovalActivities",
  garminManualUpdates: "GarminManualUpdates"
};

export const FIRESTORE_COLLECTIONS_NAMES = {
  challenges: "Challenges",
  challengeUsersCollectionGroup: "ChallengeUsers",
  navLive: "NavLive",
  archivedNavs: "ArchivedNavs",
  pointLive: "PointLive",
  activities: "Activities",
  activityCollectionGroup: "Activity",
  pointsActivities: "Points",
  errors: "Errors",
  reports: "Reports",
  logs: "Logs"
};

export const INPUT_TYPES = {
  text: "text",
  password: "password"
};

export const SIGNIN_TYPES = {
  emailAndPassword: "EmailAndPassword",
  google: "Google",
  facebook: "Facebook",
  apple: "Apple"
};

export const LOCAL_STORAGE_KEYS = {
  signInType: "signInType",
  uid: "uid",
  user: "user",
  accessToken: "accessToken",
  idToken: "idToken",
  settingSpeedUnit: "settingSpeedUnit",
  settingDistanceUnit: "settingDistanceUnit",
  settingWindUnit: "settingWindUnit",
  diaryScrollIndex: "diaryScrollIndex",
  diaryFilter: "diaryFilter",
  statisticsScrollY: "statisticsScrollY",
  statisticsFilter: "statisticsFilter",
  statisticsFilterGroupBy: "statisticsFilterGroupBy",
  cutTrackToolStatus: "cutTrackToolStatus",
  settingFoilingSpeed: "settingFoilingSpeed",
  mapType: "mapType",
  sportRecorded: "sportRecorded",
  deleteCountAllowed: "deleteCountAllowed",
  cachedLocation: "cachedLocation",
  manualManeuvers: "manualManeuvers",
  /** Prefix for per-activity map download URL cache entries on Android (`${prefix}${userId}:...`). */
  activityMapUrlCachePrefix: "wsActMapUrl:"
};

export const LOG_LEVELS = {
  info: "info",
  error: "error",
  warning: "warning"
};

export const DATE_FORMATS = {
  DD_MM_YYYY: "DD/MM/YYYY",
  DD_MM_YYYY_HH_MM_SS: "DD/MM/YYYY HH:mm:ss",
  DDMMYYYY: "DDMMYYYY",
  DDMMMYYYY: "DDMMMYYYY",
  DDMMMMYYYY: "DDMMMMYYYY",
  DDMMMYYYYATHMM_Meridiem: "DDMMMYYYYATHMM_Meridiem",
  YYYYMMDDHHMMSSPlus0000: "YYYYMMDDHHMMSSPlus0000",
  YYYYMMDDTHHMMSSZ: "YYYYMMDDTHHMMSSZ",
  HMM_Meridiem: "HMM_Meridiem",
  HMMSS_Meridiem: "HMMSS_Meridiem",
  YYYY_MM_DD: "YYYY_MM_DD",
  DDSuffix_MM: "DDSuffix_MM"
};

export const TIME_FORMATS = {
  HHMM: "HHMM",
  HHMMSS: "HHMMSS"
};

export const SETTING_DEFAULT_UNITS = {
  speed: "Knots",
  distance: "Km",
  wind: "Knots"
};

export const SETTING_DISTANCE_UNITS = {
  kilometers: "Km",
  n_dot_Miles: "N.Miles",
  miles: "Miles"
};

export const DATABASE_SETTING_DISTANCE_UNITS = {
  Meters: "m",
  [SETTING_DISTANCE_UNITS["kilometers"]]: "km",
  [SETTING_DISTANCE_UNITS["n_dot_Miles"]]: "NM",
  [SETTING_DISTANCE_UNITS["miles"]]: "mi"
};

export const FROM_DATABASE_DISTANCE_UNITS = {
  0: "Meters",
  1: SETTING_DISTANCE_UNITS["kilometers"],
  2: SETTING_DISTANCE_UNITS["n_dot_Miles"],
  3: SETTING_DISTANCE_UNITS["miles"]
};

export const TO_DATABASE_DISTANCE_UNITS = Object.keys(FROM_DATABASE_DISTANCE_UNITS).reduce(
  (acc: Record<string, string>, key) => {
    acc[FROM_DATABASE_DISTANCE_UNITS[key]] = key;
    return acc;
  },
  {}
);

export const SETTING_SPEED_UNITS = {
  km_slash_h: "km/h",
  knots: "Knots",
  mph: "mph",
  m_slash_s: "m/s"
};

export const DIRECTIONS = {
  N: "N",
  NE: "NE",
  E: "E",
  SE: "SE",
  S: "S",
  SW: "SW",
  W: "W",
  NW: "NW",
  North: "North",
  NorthEast: "North East",
  East: "East",
  SouthEast: "South East",
  South: "South",
  SouthWest: "South West",
  West: "West",
  NorthWest: "North West"
};

export const CHALLENGE_TYPES = [
  {label: "Accumulated distance (km)", value: 0},
  {label: "Max distance (km)", value: 1},
  {label: "Max speed in 5 seconds (km/h)", value: 2},
  {label: "Number of activities", value: 3},
  {label: "Successful jibes ratio (%)", value: 4},
  {label: "Successful tacks ratio (%)", value: 5},
  {label: "Best average speed (km/h)", value: 6},
  {label: "Best time covering distance", value: 7},
  {label: "Average speed (km/h) over distance", value: 8}
];

export const CHALLENGE_MAX_NB_ACTIVITIES_LIMIT = 100000;

export const CHALLENGE_TYPES_DISTANCE_UNITS = Object.entries(FROM_DATABASE_DISTANCE_UNITS).map(([value, key]) => {
  return {value: parseInt(value), label: key};
});

export const challengeTypeBeauty = (param: ChallengeDistanceParam, t: TranslateFunction): string[] => {
  const distanceUnit = UnitService.getDistanceUnit();
  const {distance, unit = 0} = param;

  // Check if same unit, otherwise convert
  let suffix = "";
  let prefix = "";
  if (
    distanceUnit !== "" &&
    distanceUnit !== undefined &&
    distance !== undefined &&
    unit !== undefined &&
    unit !== "" &&
    distance != null &&
    unit != null
  ) {
    if (unit !== TO_DATABASE_DISTANCE_UNITS[distanceUnit]) {
      suffix = ` (${ConvertUtil.convertDistance(distance / 1000, distanceUnit).toFixed(1)}${DATABASE_SETTING_DISTANCE_UNITS[distanceUnit]})`;
      prefix = `${ConvertUtil.convertDistance(distance / 1000, FROM_DATABASE_DISTANCE_UNITS[unit])}${DATABASE_SETTING_DISTANCE_UNITS[FROM_DATABASE_DISTANCE_UNITS[unit]]}`;
    }
  }

  return [
    t("challenges.challengeTypeMaximumAccumulatedDistance"),
    t("challenges.challengeTypeMaximumDistance"),
    t("challenges.challengeTypeTopSpeed5s"),
    t("challenges.challengeTypeNumberActivities"),
    t("challenges.challengeTypeSuccessfulJibesRatio"),
    t("challenges.challengeTypeSuccessfulTacksRatio"),
    t("challenges.challengeTypeBestAverageSpeed"),
    // Param is meters here
    t("challenges.challengeTypeBestTimeCovering", {distance: `${prefix}${suffix}`}),
    t("challenges.challengeTypeAverageSpeedOver", {distance: `${prefix}${suffix}`})
  ];
};

export const challengeTypesBeautyUnits = (param: ChallengeDistanceParam, t: TranslateFunction): string[] => {
  const speedUnit = UnitService.getSpeedUnit();
  const distanceUnit = UnitService.getDistanceUnit();
  const speedUnitLabel = getLocalizedUnit(speedUnit, t);
  const distanceUnitLabel = getLocalizedUnit(distanceUnit, t);

  const {distance = 0, unit = 0} = param;

  // Check if same unit, otherwise convert
  let suffix = "";
  let prefix = "";
  if (
    distanceUnit !== "" &&
    distanceUnit !== undefined &&
    distance !== "" &&
    distance !== undefined &&
    unit !== undefined &&
    unit !== "" &&
    distance != null &&
    unit != null
  ) {
    if (unit !== TO_DATABASE_DISTANCE_UNITS[distanceUnit]) {
      suffix = ` (${ConvertUtil.convertDistance(distance / 1000, distanceUnit).toFixed(1)}${DATABASE_SETTING_DISTANCE_UNITS[distanceUnit]})`;
      prefix = `${ConvertUtil.convertDistance(distance / 1000, FROM_DATABASE_DISTANCE_UNITS[unit])}${DATABASE_SETTING_DISTANCE_UNITS[FROM_DATABASE_DISTANCE_UNITS[unit]]}`;
    }
  }

  return [
    t("challenges.challengeTypeMaximumAccumulatedDistanceWithUnit", {unit: distanceUnitLabel}),
    t("challenges.challengeTypeMaximumDistanceWithUnit", {unit: distanceUnitLabel}),
    t("challenges.challengeTypeTopSpeed5sWithUnit", {unit: speedUnitLabel}),
    t("challenges.challengeTypeNumberActivities"),
    t("challenges.challengeTypeSuccessfulJibesRatioPercent"),
    t("challenges.challengeTypeSuccessfulTacksRatioPercent"),
    t("challenges.challengeTypeBestAverageSpeedWithUnit", {unit: speedUnitLabel}),
    t("challenges.challengeTypeBestTimeCovering", {distance: `${prefix}${suffix}`}),
    t("challenges.challengeTypeAverageSpeedOverWithUnit", {unit: speedUnitLabel, distance: `${prefix}${suffix}`})
  ];
};

export const getBestOrAverageOrSumStatBetweenAllStats = (stats: number[], challengeType: number): number | null => {
  // Depending of the challenge, get the best or the average or the sum of all stats
  // For distance, we want the sum
  // For speed, we want the average
  // For ratio, we want the average
  // For number of activities, we want the sum
  // For best time covering distance, we want the best
  // For average speed over distance, we want the average

  if (!stats || stats.length === 0) return null;

  switch (challengeType) {
    case 0: // Accumulated distance -> sum
    case 3: // Number of activities -> sum
    case 1: // Max distance -> sum
      return sum(stats);
    case 2: // Max speed 5s -> average
    case 4: // Successful jibes ratio -> average
    case 5: // Successful tacks ratio -> average
    case 6: // Best average speed -> average
    case 8: // Average speed over distance -> average
    case 7: // Best time covering distance -> average (min)
      return sum(stats) / stats.length;
    default:
      return null;
  }
};

export const challengeTypesBeautyUnitsForBestAverageOrSum = (
  param: ChallengeDistanceParam,
  t: TranslateFunction
): string[] => {
  const speedUnit = UnitService.getSpeedUnit();
  const distanceUnit = UnitService.getDistanceUnit();
  const speedUnitLabel = getLocalizedUnit(speedUnit, t);
  const distanceUnitLabel = getLocalizedUnit(distanceUnit, t);

  const {distance = 0, unit = 0} = param;

  // Check if same unit, otherwise convert
  let suffix = "";
  let prefix = "";
  if (
    distanceUnit !== "" &&
    distanceUnit !== undefined &&
    distance !== "" &&
    distance !== undefined &&
    unit !== undefined &&
    unit !== "" &&
    distance != null &&
    unit != null
  ) {
    if (unit !== TO_DATABASE_DISTANCE_UNITS[distanceUnit]) {
      suffix = ` (${ConvertUtil.convertDistance(distance / 1000, distanceUnit).toFixed(1)}${DATABASE_SETTING_DISTANCE_UNITS[distanceUnit]})`;
      prefix = `${ConvertUtil.convertDistance(distance / 1000, FROM_DATABASE_DISTANCE_UNITS[unit])}${DATABASE_SETTING_DISTANCE_UNITS[FROM_DATABASE_DISTANCE_UNITS[unit]]}`;
    }
  }

  return [
    t("challenges.challengeTypeTotalAccumulatedDistanceWithUnit", {unit: distanceUnitLabel}),
    t("challenges.challengeTypeTotalMaxDistanceWithUnit", {unit: distanceUnitLabel}),
    t("challenges.challengeTypeAvgTopSpeed5sWithUnit", {unit: speedUnitLabel}),
    t("challenges.challengeTypeTotalNumberActivities"),
    t("challenges.challengeTypeAvgSuccessfulJibesRatioPercent"),
    t("challenges.challengeTypeAvgSuccessfulTacksRatioPercent"),
    t("challenges.challengeTypeAvgBestAverageSpeedWithUnit", {unit: speedUnitLabel}),
    t("challenges.challengeTypeAvgTimeCovering", {distance: `${prefix}${suffix}`}),
    t("challenges.challengeTypeAvgSpeedOverWithUnit", {unit: speedUnitLabel, distance: `${prefix}${suffix}`})
  ];
};

export const challengeTypesConvertUnitsForView = (): Array<(value: any) => any> => {
  const speedUnit = UnitService.getSpeedUnit();
  const distanceUnit = UnitService.getDistanceUnit();

  return [
    (value) => ConvertUtil.convertDistance(value / 1000, distanceUnit).toFixed(2),
    (value) => ConvertUtil.convertDistance(value / 1000, distanceUnit).toFixed(2),
    (value) => ConvertUtil.convertSpeed(value * 3.6, speedUnit).toFixed(2),
    (value) => Math.round(value),
    (value) => (value * 100).toFixed(1),
    (value) => (value * 100).toFixed(1),
    (value) => ConvertUtil.convertSpeed(value * 3.6, speedUnit).toFixed(2),
    (value) => (value ? DateTimeUtil.convertToPeriod(value, false, true, false) : null),
    (value) => ConvertUtil.convertSpeed(value * 3.6, speedUnit).toFixed(2)
  ];
};

export const challengeTypesConvertUnitsForViewIframe = (): Array<(value: any) => any> => {
  const distanceUnit = UnitService.getDistanceUnit();

  return [
    (value) => ConvertUtil.convertDistance(value / 1000, distanceUnit).toFixed(2),
    (value) => ConvertUtil.convertDistance(value / 1000, distanceUnit).toFixed(2),
    (value) => ConvertUtil.convertSpeed(value * 3.6, SETTING_SPEED_UNITS.knots).toFixed(2),
    (value) => Math.round(value),
    (value) => (value * 100).toFixed(1),
    (value) => (value * 100).toFixed(1),
    (value) => ConvertUtil.convertSpeed(value * 3.6, SETTING_SPEED_UNITS.knots).toFixed(2),
    (value) => (value ? DateTimeUtil.convertToPeriod(value, false, true, false) : null),
    (value) => ConvertUtil.convertSpeed(value * 3.6, SETTING_SPEED_UNITS.knots).toFixed(2)
  ];
};

export const speedUnitsForChallengeTypeIframe = (challengeType: number, t: TranslateFunction): string => {
  return [2, 6, 8].includes(challengeType)
    ? getLocalizedUnit(SETTING_SPEED_UNITS.knots, t)
    : getLocalizedUnit(CHALLENGE_SUFFIX_UNITS[challengeType], t);
};

export const CHALLENGE_SUFFIX_UNITS = ["km", "km", "km/h", "", "%", "%", "km/h", "", "km/h"];

export const CHALLENGE_TYPES_CONVERT_UNIT_FROM_DB_TO_VIEW: {[challengeType: number]: (value: any) => any} = {
  0: (value) => (value / 1000).toFixed(2),
  1: (value) => (value / 1000).toFixed(2),
  2: (value) => (value * 3.6).toFixed(2),
  3: (value) => value,
  4: (value) => (value * 100).toFixed(0),
  5: (value) => (value * 100).toFixed(0),
  6: (value) => (value * 3.6).toFixed(2),
  7: (value) => DateTimeUtil.convertToPeriod(value, false, true),
  8: (value) => (value * 3.6).toFixed(2)
};

const challengesTypeComputeBasic = (
  entries: ChallengeActivityEntry[],
  inKm = false,
  withActivityId = false,
  isLess = false,
  defaultStat: number = 0
): ChallengeBestStat | number | null => {
  const best: ChallengeBestStat = {statistic: defaultStat, activityId: null};
  for (const entry of entries) {
    if (
      !isNil(entry.statistic) &&
      entry.isActive &&
      ((!isLess && entry.statistic > best.statistic) || (isLess && entry.statistic < best.statistic))
    ) {
      best.statistic = entry.statistic;
      best.activityId = entry.activityID;
    }
  }

  if (
    best.statistic &&
    (best.statistic === -Infinity ||
      isNaN(best.statistic) ||
      best.statistic === Infinity ||
      best.statistic < 0 ||
      Array.isArray(best.statistic))
  ) {
    console.warn(`Invalid statistic ${best.statistic} found for activity ${best.activityId}, skipping`);

    best.statistic = null;
    best.activityId = null;
  }

  if (withActivityId) {
    return best;
  } else {
    return best.statistic;
  }
};

export const challengesTypeCompute = (
  inKm = false,
  withActivityId = false
): {[challengeType: number]: (entries: ChallengeActivityEntry[]) => ChallengeBestStat | number | null} => ({
  /**
   * Compute the best stats depending on the challenge type
   * If inKm is true, the result will be in km
   * If withActivityId is true, the result will be an object with the best activity id so we need to compute the stats manually without using sum or max
   * @param {Array<object>} entries Activity entries, each with `statistic`, `isActive` and `activityID`
   * @return {object|number} `{statistic, activityId}` when withActivityId is set, otherwise the statistic
   */
  0: (entries) => {
    // Accumulated distance, we sum all the distances, activityId is not needed
    const best = {statistic: 0, activityId: null};
    const _sum = sum(entries.map((entry) => (entry.isActive ? entry.statistic : 0)));
    if (_sum) {
      if (inKm) {
        best.statistic = _sum / 1000;
      } else {
        best.statistic = _sum;
      }
    } else {
      best.statistic = 0;
    }

    if (withActivityId) {
      return best;
    } else {
      return best.statistic;
    }
  },
  1: (entries) => {
    // Max distance, we get the max distance, activityId is needed
    const best = {statistic: 0, activityId: null};
    for (const entry of entries) {
      if (entry.isActive && entry.statistic > best.statistic) {
        best.statistic = entry.statistic;
        best.activityId = entry.activityID;
      }
    }

    if (best.statistic && best.statistic !== -Infinity && !isNaN(best.statistic)) {
      if (inKm) {
        best.statistic = best.statistic / 1000;
      }
    } else {
      best.statistic = 0;
      best.activityId = null;
    }

    if (withActivityId) {
      return best;
    } else {
      return best.statistic;
    }
  },
  2: (entries) => {
    // Max speed, we get the max speed, activityId is needed
    return challengesTypeComputeBasic(entries, inKm, withActivityId);
  },
  3: (entries) => {
    // Number of activities, we count the number of activities, activityId is not needed
    // Start with 0 or the isFake stat
    const isFakeEntry = entries.find((entry) => entry.isFake);
    const best = {statistic: isFakeEntry ? isFakeEntry.statistic : 0, activityId: null};

    for (const entry of entries) {
      if (entry.isActive) {
        best.statistic++;
      }
      if (entry.isFake) {
        best.statistic--;
      }
    }

    if (withActivityId) {
      return best;
    } else {
      return best.statistic;
    }
  },
  4: (entries) => {
    return challengesTypeComputeBasic(entries, inKm, withActivityId);
  },
  5: (entries) => {
    return challengesTypeComputeBasic(entries, inKm, withActivityId);
  },
  6: (entries) => {
    // Best avg speed, we get the best avg speed, activityId is needed
    return challengesTypeComputeBasic(entries, inKm, withActivityId);
  },
  7: (entries) => {
    // Best time over distance, we get the best (ie min) time over distance, activityId is needed
    return challengesTypeComputeBasic(entries, inKm, withActivityId, true, Infinity);
  },
  8: (entries) => {
    // Average speed over distance, we get the average speed over distance, activityId is needed
    return challengesTypeComputeBasic(entries, inKm, withActivityId);
  }
});

export const CHALLENGE_TYPES_GET_STATISTIC: {[challengeType: number]: (activity: any, inKm?: boolean) => number} = {
  /**
   * It returns the statistic value depending on the challenge type
   * @param {object} activity The activity to read the statistic from
   * @param {boolean} [inKm=false] If true, the result will be in km
   * @return {number}
   */
  0: (activity, inKm = false) => (inKm ? activity.totalDistanceInKm : activity.totalDistanceInKm * 1000),
  1: (activity, inKm = false) => (inKm ? activity.totalDistanceInKm : activity.totalDistanceInKm * 1000),
  2: (activity) => ConvertUtil.convertSpeed(activity.bestSpeedFiveSecondsKM, SETTING_SPEED_UNITS.m_slash_s),
  3: (activity) => 1,
  4: (activity) => {
    const jibesLocations = activity.tacksAndJibesService?.getJibes?.() ?? [];
    const totalJibes = jibesLocations.length;
    const successfulJibes = jibesLocations.filter((jibe) => jibe.successful).length;
    if (totalJibes) {
      return successfulJibes / totalJibes;
    }
    return 0;
  },
  5: (activity) => {
    const tacksLocations = activity.tacksAndJibesService?.getTacks?.() ?? [];
    const totalTacks = tacksLocations.length;
    const successfulTacks = tacksLocations.filter((tack) => tack.successful).length;
    if (totalTacks) {
      return successfulTacks / totalTacks;
    }
    return 0;
  },
  6: (activity) => ConvertUtil.convertSpeed(activity.averageSpeedInKM, SETTING_SPEED_UNITS.m_slash_s),
  7: (activity) => activity.minTimeOverDistance,
  8: (activity) => activity.averageSpeedOverDistance
};

export const CHALLENGE_TYPES_GET_ORIGINAL_STATISTIC: {[challengeType: number]: (activity: any) => Record<string, any>} =
  {
    /**
     * It returns the statistic in the original key value depending on the challenge type
     * @param {object} activity The activity to read the statistic from
     * @return {object}
     */
    0: (activity) => ({dT: activity.statistic / 1000}),
    1: (activity) => ({dT: activity.statistic / 1000}),
    2: (activity) => ({
      bs5: ConvertUtil.convertSpeedInKM(activity.statistic, SETTING_SPEED_UNITS.m_slash_s)
    }),
    3: (activity) => ({}),
    4: (activity) => ({}),
    5: (activity) => ({}),
    6: (activity) => ({
      sA: ConvertUtil.convertSpeedInKM(activity.statistic, SETTING_SPEED_UNITS.m_slash_s)
    }),
    7: (activity) => ({
      mTOdT: activity.statistic
    }),
    8: (activity) => ({
      aSOdT: activity.statistic
    })
  };

export const CONTACT_TYPES = {
  EMAIL: 0,
  PHONE: 1,
  WHATSAPP_GROUP: 2,
  WHATSAPP: 3,
  PHONE_CALL: 4,
  PHONE_SMS: 5,
  FACEBOOK: 6,
  INSTAGRAM: 7
};
